import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFormChecklist, createFormChecklist, updateFormChecklist } from '../formChecklistsService';

let _uidCounter = 0;
const genUid = () => String(++_uidCounter);

const defaultFields = {
    name: '',
    activity: '',
    condition: '',
    standard: '',
    allowSave: false,
    allowClear: false,
};

export function useFormChecklistForm({ userIsIccSupportTeam } = {}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [fields, setFields] = useState(defaultFields);
    const [steps, setSteps] = useState([]);
    const [loaded, setLoaded] = useState(!isEditMode);
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (!isEditMode) return;
        let cancelled = false;
        const load = async () => {
            try {
                const data = await getFormChecklist(id);
                if (cancelled) return;
                setFields({
                    name: data.name || '',
                    activity: data.activity || '',
                    condition: data.condition || '',
                    standard: data.standard || '',
                    allowSave: data.allowSave || false,
                    allowClear: data.allowClear || false,
                });
                setSteps(
                    (data.steps || [])
                        .sort((a, b) => a.order - b.order)
                        .map(step => ({
                            _uid: genUid(),
                            id: step.id,
                            name: step.name,
                            tasks: (step.tasks || [])
                                .sort((a, b) => a.order - b.order)
                                .map(task => ({
                                    _uid: genUid(),
                                    id: task.id,
                                    description: task.description,
                                })),
                        }))
                );
            } catch (err) {
                setSubmitError(err.message);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [id, isEditMode]);

    const handleFieldChange = (key, value) => {
        setFields(prev => ({ ...prev, [key]: value }));
        if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: null }));
    };

    const addStep = (atIndex) => {
        const newStep = { _uid: genUid(), name: '', tasks: [] };
        setSteps(prev => {
            if (atIndex === undefined) return [...prev, newStep];
            const next = [...prev];
            next.splice(atIndex, 0, newStep);
            return next;
        });
    };

    const removeStep = (stepIdx) => {
        setSteps(prev => prev.filter((_, i) => i !== stepIdx));
    };

    const updateStep = (stepIdx, key, value) => {
        setSteps(prev => prev.map((step, i) => i === stepIdx ? { ...step, [key]: value } : step));
    };

    const reorderSteps = (fromIdx, toIdx) => {
        setSteps(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };

    const addTask = (stepIdx) => {
        setSteps(prev => prev.map((step, i) => {
            if (i !== stepIdx) return step;
            return { ...step, tasks: [...step.tasks, { _uid: genUid(), description: '' }] };
        }));
    };

    const removeTask = (stepIdx, taskIdx) => {
        setSteps(prev => prev.map((step, i) => {
            if (i !== stepIdx) return step;
            return { ...step, tasks: step.tasks.filter((_, ti) => ti !== taskIdx) };
        }));
    };

    const updateTask = (stepIdx, taskIdx, key, value) => {
        setSteps(prev => prev.map((step, i) => {
            if (i !== stepIdx) return step;
            return {
                ...step,
                tasks: step.tasks.map((task, ti) =>
                    ti === taskIdx ? { ...task, [key]: value } : task
                ),
            };
        }));
    };

    const reorderTasks = (stepIdx, fromIdx, toIdx) => {
        setSteps(prev => prev.map((step, i) => {
            if (i !== stepIdx) return step;
            const next = [...step.tasks];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return { ...step, tasks: next };
        }));
    };

    const validateLocally = () => {
        const errors = {};
        return errors;
    };

    const buildStepsPayload = () =>
        steps.map((step, stepIdx) => ({
            name: step.name,
            order: stepIdx + 1,
            tasks: step.tasks.map((task, taskIdx) => ({
                description: task.description,
                order: taskIdx + 1,
            })),
        }));

    const handleSubmit = async () => {
        const localErrors = validateLocally();
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            return;
        }

        setSaving(true);
        setFieldErrors({});
        setSubmitError(null);

        try {
            let result;
            if (!isEditMode) {
                result = await createFormChecklist({ ...fields, steps: buildStepsPayload() });
            } else {
                result = await updateFormChecklist(id, { ...fields, steps: buildStepsPayload() });
            }
            navigate(`/documents/form-checklists/${result.id}`);
        } catch (err) {
            if (err.fieldErrors && typeof err.fieldErrors === 'object') {
                const parsedErrors = {};
                Object.entries(err.fieldErrors).forEach(([key, value]) => {
                    if (key !== 'detail') parsedErrors[key] = Array.isArray(value) ? value : [value];
                });
                if (Object.keys(parsedErrors).length > 0) {
                    setFieldErrors(parsedErrors);
                    return;
                }
            }
            setSubmitError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return {
        id,
        isEditMode,
        fields,
        steps,
        loaded,
        saving,
        fieldErrors,
        submitError,
        handleFieldChange,
        addStep,
        removeStep,
        updateStep,
        reorderSteps,
        addTask,
        removeTask,
        updateTask,
        reorderTasks,
        handleSubmit,
    };
}
